import type { GridSortDirection } from '@mui/x-data-grid-pro'

/*
  Shared Table column helpers (working-conventions §36: extracted on the second hand-roll).
  First written in IntegrationsPage/RoadmapTab.tsx (VRG-688), copied into
  GtmArticlesPage/ContentIdeasPanel.tsx (VRG-740); both now read these.
*/

/**
 * `isAnyOf` as SET MEMBERSHIP for a column whose value is a list (solutions, industries,
 * categories). MUI's stock `isAnyOf` compares the whole array, so a row tagged
 * [a, b] would never match a filter for `a`. Pass as the column's `filterOperators`.
 */
export const membershipFilter = <Row>(itemsOf: (row: Row) => readonly string[]) => [
  {
    value: 'isAnyOf',
    getApplyFilterFn: (filterItem: { value?: unknown }) => {
      const wanted = Array.isArray(filterItem.value) ? filterItem.value : []
      if (!wanted.length) return null
      return (_value: unknown, row: Row) => itemsOf(row).some((v) => wanted.includes(v))
    },
  },
]

type SortParams = { api: { getRow: (id: unknown) => unknown }; id: unknown }

/**
 * A `getSortComparator` that keeps EMPTY values last in both directions. MUI negates the
 * comparator for `desc`, which would float every unranked row to the top of a descending sort;
 * `getSortComparator` receives the direction, so the null rule can ignore it. `compare`
 * defaults to numeric.
 */
export const nullsLastComparator =
  <Row, T = number>(
    rank: (row: Row) => T | null,
    compare: (a: T, b: T) => number = (a, b) => (a as unknown as number) - (b as unknown as number),
  ) =>
  (direction: GridSortDirection) =>
  (_a: unknown, _b: unknown, p1: SortParams, p2: SortParams) => {
    const a = rank(p1.api.getRow(p1.id) as Row)
    const b = rank(p2.api.getRow(p2.id) as Row)
    if (a === null && b === null) return 0
    if (a === null) return 1
    if (b === null) return -1
    const c = compare(a, b)
    return direction === 'desc' ? -c : c
  }
